/**
 * Where the save's copies are kept. The durable backend is asynchronous (IndexedDB on the web, Capacitor Preferences
 * in the iOS shell); the mirror is localStorage, synchronous, so a write made as the page goes away (pagehide) lands
 * even when the backend's transaction never completes.
 */

/** The durable copy: survives what localStorage does not (its 5 MB cap, WebKit's storage pressure). */
export interface SaveBackend {
  /** For diagnostics ("indexeddb", "preferences", "memory"). */
  readonly name: string;
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
}

/** The synchronous copy (localStorage); every call may throw (private mode, quota). */
export interface SaveMirror {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** One IndexedDB database with one object store of strings: all the save ever needs. */
export class IdbBackend implements SaveBackend {
  static readonly DbName = "highmarch-save";
  static readonly StoreName = "kv";
  /** Bumped only when the object stores change. */
  static readonly DbVersion = 1;
  /**
   * Some WebKit releases (the Safari 14 era) let `indexedDB.open` fire none of its events: without a limit the boot
   * waited on it forever behind a black page. Past this the open counts as refused and the save runs on the mirror.
   */
  static readonly OpenTimeoutMs = 4000;
  readonly name = "indexeddb";

  private constructor(private readonly db: IDBDatabase) {}

  /** Opens (creating on first use) the database; rejects when IndexedDB is missing, refused or silent too long. */
  static open(factory: IDBFactory | undefined = globalThis.indexedDB, timeoutMs = IdbBackend.OpenTimeoutMs): Promise<IdbBackend> {
    return new Promise((resolve, reject) => {
      if (!factory) { reject(new Error("IndexedDB unavailable")); return; }
      let req: IDBOpenDBRequest;
      try { req = factory.open(IdbBackend.DbName, IdbBackend.DbVersion); } catch (err) { reject(err); return; }
      let settled = false;
      const settle = (fn: () => void) => { if (settled) return; settled = true; clearTimeout(timer); fn(); };
      const timer = setTimeout(() => settle(() => reject(new Error("IndexedDB open timed out"))), timeoutMs);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IdbBackend.StoreName)) req.result.createObjectStore(IdbBackend.StoreName);
      };
      // An open that succeeds after the timeout is closed: the boot has moved on to the mirror.
      req.onsuccess = () => { if (settled) req.result.close(); else settle(() => resolve(new IdbBackend(req.result))); };
      req.onerror = () => settle(() => reject(req.error ?? new Error("IndexedDB open failed")));
      // Another tab holds an older version open: it never happens with one version, but must not hang the boot.
      req.onblocked = () => settle(() => reject(new Error("IndexedDB open blocked")));
    });
  }

  get(key: string): Promise<string | null> {
    return this.run("readonly", (s) => s.get(key), (v) => (typeof v === "string" ? v : null));
  }
  set(key: string, value: string): Promise<void> {
    return this.run("readwrite", (s) => s.put(value, key), () => undefined);
  }
  delete(key: string): Promise<void> {
    return this.run("readwrite", (s) => s.delete(key), () => undefined);
  }

  /** One request in its own transaction, resolved when the transaction commits (a put is durable only then). */
  private run<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest, map: (v: unknown) => T): Promise<T> {
    return new Promise((resolve, reject) => {
      let tx: IDBTransaction;
      try { tx = this.db.transaction(IdbBackend.StoreName, mode); } catch (err) { reject(err); return; }
      const req = op(tx.objectStore(IdbBackend.StoreName));
      tx.oncomplete = () => resolve(map(req.result));
      tx.onerror = () => reject(tx.error ?? req.error ?? new Error("IndexedDB transaction failed"));
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    });
  }
}

/** A backend in memory: the fallback when no durable storage opens (the mirror still keeps the save), and for tests. */
export class MemoryBackend implements SaveBackend {
  readonly name = "memory";
  readonly data = new Map<string, string>();
  async get(key: string): Promise<string | null> { return this.data.get(key) ?? null; }
  async set(key: string, value: string): Promise<void> { this.data.set(key, value); }
  async delete(key: string): Promise<void> { this.data.delete(key); }
}

/** The page's localStorage behind the SaveMirror shape, or null when the browser denies it (some private modes). */
export function localStorageMirror(): SaveMirror | null {
  try {
    const ls = globalThis.localStorage;
    return ls ?? null;
  } catch { return null; }
}
